'use strict';

var d3 = require('d3');
var $ = require('jquery');

/**
 * Group
 *
 * Contains elements (bars, circles, tiles etc.) for one X scale value
 *
 * @param d3Helper
 * @param tooltip
 * @param chartGaps
 * @returns {Function}
 * @constructor
 */
var Group = function Group(d3Helper, tooltip, chartGaps, chartNoDataBars) {
  /**
   * @param scalePropertyName - a property name used to find X(Y) value
   * @param tooltipContentProvider - a function that returns HTML content of the tooltip
   * @param dispatchGroupClick - a boolean indicating if clicking should be handled for groups
   * @returns {Function}
   * @constructor
   */
  return function (scalePropertyName, tooltipContentProvider, dispatchGroupClick) {
    var scales,
      params,
      ranges = [];
    var gaps = chartGaps();
    var noDataBars = chartNoDataBars();
    var isViewport = false;
    var hooverTracked = false;
    var tip, tooltipInitialized, tooltipClasses;
    var tooltipAdded = tooltipContentProvider !== undefined;
    var dispatch = d3.dispatch('groupclick');
    if (tooltipAdded) {
      var cssClass = tooltipContentProvider.cssClass;
      tip = tooltip(tooltipContentProvider, cssClass);
      tooltipClasses = cssClass && '.' + cssClass.split(' ').join('.');
    }

    /**
     * Returns X for data
     * @param {Object} d - data item
     * @returns {(undefined|number)}
     */
    function getX(d) {
      var x = scales.xScale(d[scalePropertyName]);
      return x === undefined ? undefined : Math.round(x);
    }

    /**
     * Returns Y for data
     * @param {Object} d - data item
     * @returns {(undefined|number)}
     */
    function getY(d) {
      var y = scales.yScale(d[scalePropertyName]);
      return y === undefined ? undefined : Math.round(y);
    }

    /**
     * Checks if group is gap
     * @param {Object} d - data item
     * @returns {boolean}
     */
    function isGap(d) {
      return d.isGap;
    }

    /**
     * Checks if group is a bar without data
     * @param {Object} d - data item
     * @returns {boolean}
     */
    function isNoData(d) {
      return d.noData;
    }

    /**
     * Checks if group is visible
     * @param {Object} d - data item
     * @returns {boolean}
     */
    function isVisible(d) {
      var value = params.direction === 'vertical' ? getY(d) : getX(d);
      return value !== undefined && !d.isHidden;
    }

    /**
     * Returns true if group is in ranges
     * @param {number} x
     * @returns {boolean}
     */
    function inRanges(x) {
      for (var i = 0; i < ranges.length; i++) {
        var r = ranges[i];
        if (r[0] <= x && x < r[1]) {
          return true;
        }
      }
      return false;
    }

    /**
     * Checks if group is selected
     * @param {Object} d - data item
     * @param {number} i - index
     * @returns {boolean}
     */
    function isSelected(d, i) {
      return inRanges(i) || d.selected;
    }

    /**
     * Handler called on mouse down
     * @param {Object} d - data item
     * @param {number} i - index
     */
    var mouseDownHandler = function mouseDownHandler(d, i) {
      d3.event.preventDefault();

      // only if left button clicked
      if (d3.event.button === 0) {
        var targetData = d3.event.target.__data__;
        dispatch.groupclick(d, i, targetData, d3.event);
      }
    };

    /**
     * Handler called on mouse over
     * @param {Object} d - data item
     */
    var mouseOverHandler = function mouseOverHandler(d) {
      if (tooltipAdded) {
        tip.show(d, this);
        tip.setPositionNearCursor(tooltipClasses);
      }
      if (hooverTracked) {
        d3.select(this.parentNode).classed('groups-hover', true);
        d3.select(this).classed('stack-hover', true);
      }
    };
    var mouseMoveHandler = function mouseMoveHandler() {
      if (tooltipAdded) {
        tip.setPositionNearCursor(tooltipClasses);
      }
    };

    /**
     * Handler called on mouse out
     */
    var mouseOutHandler = function mouseOutHandler() {
      if (tooltipAdded) {
        tip.hideAndAdjustPosition();
      }
      if (hooverTracked) {
        d3.select(this.parentNode).classed('groups-hover', false);
        d3.select(this).classed('stack-hover', false);
      }
    };

    /**
     * Binds event to group DOM element
     * @param selection
     */
    function bindEvents(selection) {
      if (dispatchGroupClick) {
        selection.on('mousedown', mouseDownHandler);
      }
      if (tooltipAdded || hooverTracked) {
        selection.on('mouseout', mouseOutHandler);
        selection.on('mousemove', mouseMoveHandler);
        selection.on('mouseover', mouseOverHandler);
      }
    }
    function unbindEvents(selection) {
      selection.on('mousedown', null);
      selection.on('mouseout', null);
      selection.on('mousemove', null);
      selection.on('mouseover', null);
    }

    /**
     *
     * @param container
     */
    var group = function group(container) {
      var groups = d3Helper.createGroupElement(container, 'groups');
      groups.classed({
        'viewport-groups': isViewport
      });
      var stacks = groups.selectAll('.stack').data(container.datum());
      stacks.enter().append('g').call(bindEvents);
      stacks.exit().call(unbindEvents).remove();
      stacks.selectAll('rect.gap').remove();
      stacks.selectAll('rect.noData').remove();
      stacks.classed({
        stack: true,
        gap: isGap,
        noData: isNoData,
        visible: isVisible,
        selected: isSelected
      }).attr({
        'opacity': function opacity(d) {
          return d.opacity;
        },
        'data-bar-label': function dataBarLabel(d) {
          return d.stackLabel;
        },
        'visibility': function visibility(d) {
          return isVisible(d) ? 'visible' : 'hidden';
        },
        'transform': function transform(d) {
          var transform = null;
          if (params.direction === 'vertical') {
            var y = getY(d);
            transform = y !== undefined ? 'translate(' + [0, y] + ')' : null;
          } else {
            var x = getX(d);
            transform = x !== undefined ? 'translate(' + [x, 0] + ')' : null;
          }
          return transform;
        }
      });
      groups.call(gaps.params(params).scales(scales));
      groups.call(noDataBars.params(params).scales(scales));
      if (!tooltipInitialized && tooltipAdded) {
        container.call(tip);
        tooltipInitialized = true;
      }
    };

    /**
     * Enables hoover tracking.
     * @returns {group}
     */
    group.withHooverTracking = function () {
      hooverTracked = true;
      return group;
    };

    /**
     * Sets/gets group's ranges
     * @param value
     * @returns {group}
     */
    group.withRanges = function (value) {
      ranges = value;
      return group;
    };

    /**
     * Sets/gets group's scales
     * @param value
     * @returns {group}
     */
    group.scales = function (value) {
      scales = value;
      return group;
    };

    /**
     * Sets/gets group's params
     * @param value
     * @returns {group}
     */
    group.params = function (value) {
      params = value;
      return group;
    };

    /**
     * Sets/gets group's isViewport (when group is rendered on viewport chart)
     * @returns {group}
     */
    group.asViewport = function () {
      isViewport = true;
      return group;
    };

    /**
     * Change group's property name data to get value from scale.
     * @param value
     * @returns {group}
     */
    group.withPropertyName = function (value) {
      scalePropertyName = value;
      return group;
    };
    return d3.rebind(group, dispatch, 'on');
  };
};
Group.$inject = ['d3Helper', 'ChartElementsTooltip', 'ChartElementsGaps', 'ChartElementsNoDataBars'];
module.exports = Group;